// Phase SE-8 — Pure helpers for the extraction history view.
//
// Filtering + formatting only; no I/O.

import type { ExtractionHistoryRow } from "./useExtractionHistory.ts";

export type ExtractionStatusFilter =
  | "all"
  | "complete"
  | "declined"
  | "queued"
  | "processing";

export type ExtractionModeFilter = "all" | "paste" | "upload";

export interface ExtractionHistoryFilter {
  /** Free-text search across user_prompt + plan_summary. */
  query: string;
  status: ExtractionStatusFilter;
  mode: ExtractionModeFilter;
  /** ISO date string (yyyy-mm-dd) or null for unbounded. */
  dateFrom: string | null;
  dateTo: string | null;
}

export const EMPTY_FILTER: ExtractionHistoryFilter = {
  query: "",
  status: "all",
  mode: "all",
  dateFrom: null,
  dateTo: null,
};

/** Apply the filter set to a row list. Pure. */
export function filterExtractionRows(
  rows: readonly ExtractionHistoryRow[],
  f: ExtractionHistoryFilter,
): ExtractionHistoryRow[] {
  const q = f.query.trim().toLowerCase();
  const fromMs = f.dateFrom ? Date.parse(`${f.dateFrom}T00:00:00`) : -Infinity;
  const toMs = f.dateTo ? Date.parse(`${f.dateTo}T23:59:59`) : Infinity;
  return rows.filter((r) => {
    if (f.status !== "all" && r.status !== f.status) return false;
    if (f.mode !== "all" && r.input_mode !== f.mode) return false;
    const ts = Date.parse(r.created_at);
    if (Number.isFinite(ts)) {
      if (ts < fromMs || ts > toMs) return false;
    }
    if (q.length > 0) {
      const haystack = `${r.user_prompt}\n${r.plan_summary ?? ""}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

/** Pure: short summary string for the collapsed row header. */
export function summariseRow(r: ExtractionHistoryRow): string {
  const kept = Array.isArray(r.selected_leg_indices)
    ? r.selected_leg_indices.length
    : 0;
  const total = Array.isArray(r.scored_legs)
    ? r.scored_legs.length
    : Array.isArray(r.extracted_legs)
      ? r.extracted_legs.length
      : 0;
  const odds = r.combined_odds_kept;
  if (r.status === "declined") return "Declined — see reason.";
  if (r.status === "complete") {
    return `${kept}/${total} legs kept${odds !== null ? ` · ${odds.toFixed(2)} combined odds` : ""}.`;
  }
  if (r.status === "queued") return "Analysis queued.";
  if (r.status === "processing") return "Processing…";
  return "Failed.";
}

/** Pure: CSV row representation for the export button. */
export function toCsvRow(r: ExtractionHistoryRow): string {
  const cells = [
    r.extraction_id,
    r.created_at,
    r.input_mode,
    r.status,
    r.user_prompt,
    r.plan_summary ?? "",
    Array.isArray(r.selected_leg_indices) ? r.selected_leg_indices.length : 0,
    Array.isArray(r.scored_legs) ? r.scored_legs.length : 0,
    r.combined_odds_kept ?? "",
    r.hit_probability_kept ?? "",
    r.expected_value_kept ?? "",
    r.declined_reason ?? "",
  ];
  return cells.map(csvEscape).join(",");
}

export const CSV_HEADER =
  "extraction_id,created_at,input_mode,status,user_prompt,plan_summary,legs_kept,legs_total,combined_odds_kept,hit_probability_kept,expected_value_kept,declined_reason";

function csvEscape(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Pure: human-readable timestamp ("2 hours ago", "3 days ago", etc.). */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return iso;
  const ageMs = now - ts;
  const min = Math.floor(ageMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  const yr = Math.floor(mo / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}
